import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useOrderPayments } from "../useHook";
import { usePermission, SystemPermissions } from "../../../Utils/permissionAccess";

function VerifyPayments() {
  const { hasPermission } = usePermission();
  const canAccept = hasPermission(SystemPermissions.Orders.ACCEPT);
  const canReject = hasPermission(SystemPermissions.Orders.REJECT);
  const canDownloadSlip = hasPermission(SystemPermissions.Orders.DOWNLOAD_SLIP);
  const canViewPending = hasPermission(SystemPermissions.Orders.VIEW_PENDING_PAYMENTS);
  const canViewApproved = hasPermission(SystemPermissions.Orders.VIEW_APPROVED_PAYMENTS);
  const canViewRejected = hasPermission(SystemPermissions.Orders.VIEW_REJECTED_PAYMENTS);
  const showPendingActions = canAccept || canReject || canDownloadSlip;

  const { getPayment, verifyPayment } = useOrderPayments();
  const [verifiedPayments, setVerifiedPayments] = useState([]);
  const [rejectedPayments, setRejectedPayments] = useState([]);
  const [acceptedPayments, setAcceptedPayments] = useState([]);
  const [activeTab, setActiveTab] = useState("pending");
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [currentAction, setCurrentAction] = useState("");

  useEffect(() => {
    if (!canViewPending && activeTab === "pending") {
      if (canViewApproved) {
        setActiveTab("approved");
      } else if (canViewRejected) {
        setActiveTab("rejected");
      }
    }
  }, [canViewPending, canViewApproved, canViewRejected]);
  useEffect(() => {
    fetchPaymentData(activeTab);
  }, [activeTab]);

  const fetchPaymentData = async (status) => {
    try {
      const response = await getPayment(status);
      const payload = response?.data || {};
      if (status === "pending") {
        setVerifiedPayments(payload.pendingPaymentScreenshots || []);
      } else if (status === "approved") {
        setAcceptedPayments(payload.approvedPaymentScreenshots || []);
      } else if (status === "rejected") {
        setRejectedPayments(payload.rejectedPaymentScreenshots || []);
      }
    } catch (error) {
      console.error("Error fetching payment data:", error);
    }
  };

  const handleAccept = (orderId) => {
    setCurrentOrderId(orderId);
    setCurrentAction("accept");
    setModalMessage(
      `Are you sure you want to accept the payment for Order ID ${orderId}?`
    );
    setShowModal(true);
  };

  const handleReject = (orderId) => {
    setCurrentOrderId(orderId);
    setCurrentAction("reject");
    setModalMessage(
      `Are you sure you want to reject the payment for Order ID ${orderId}?`
    );
    setShowModal(true);
  };

  const handleDownload = (image) => {
    const link = document.createElement("a");
    link.href = image;
    link.download = "payment_receipt.jpg";
    link.click();
  };

  const confirmAction = async () => {
    if (currentAction && currentOrderId) {
      const status = currentAction === "accept" ? "approved" : "rejected";
      try {
        const response = await verifyPayment(currentOrderId, status);
        if (response?.status === 200 || response?.data?.success) {
          const updatedOrder = verifiedPayments.find(
            (order) => order.orderId === currentOrderId
          );
          if (updatedOrder) {
            updatedOrder.status = status;
            setVerifiedPayments((prevData) =>
              prevData.filter((order) => order.orderId !== currentOrderId)
            );
            if (status === "approved") {
              setAcceptedPayments((prev) => [...prev, updatedOrder]);
            } else {
              setRejectedPayments((prev) => [...prev, updatedOrder]);
            }

            Swal.fire({
              icon: "success",
              title: `Payment ${
                currentAction === "accept" ? "Approved" : "Rejected"
              }`,
              text: `Payment for Order ID ${currentOrderId} has been successfully ${
                currentAction === "accept" ? "approved" : "rejected"
              }.`,
            });
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Failed to Update Status",
            text: "An error occurred while updating the payment status. Please try again.",
          });
        }
      } catch (error) {
        console.error("Error while updating payment status", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "An unexpected error occurred while updating the payment status.",
        });
      }
    }
    setShowModal(false);
  };

  const cancelAction = () => {
    setShowModal(false);
  };

  const renderTable = (data, isPending = false) => (
    <div className="bg-white w-full rounded-lg pagination-shadow mt-6">
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
          <thead>
            <tr className="brand-gradient text-white">
              <th className="table-th">Sr No</th>
              <th className="table-th">Order ID</th>
              <th className="table-th">Payment Type</th>
              {isPending ? (
                showPendingActions && <th className="table-th">Actions</th>
              ) : (
                <th className="table-th">Status</th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={isPending ? (showPendingActions ? 4 : 3) : 4}
                  className="text-center py-10 text-gray-500 text-lg font-medium"
                >
                  No data found
                </td>
              </tr>
            ) : (
              data.map((order, index) => (
                <tr
                  key={order.id}
                  className={`${
                    index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                  } hover:bg-gray-200 transition-colors duration-300`}
                >
                  <td className="table-td">{index + 1}</td>
                  <td className="table-td">{order.orderId}</td>
                  <td className="table-td">{order.paymentType}</td>
                  {isPending ? (
                    showPendingActions && (
                      <td className="table-td">
                        <div className="flex space-x-2">
                          {canAccept && (
                            <button
                              onClick={() => handleAccept(order.orderId)}
                              className="btn btn-primary justify-center"
                            >
                              Accept
                            </button>
                          )}
                          {canReject && (
                            <button
                              onClick={() => handleReject(order.orderId)}
                              className="btn btn-delete text-white justify-center"
                            >
                              Reject
                            </button>
                          )}
                          {canDownloadSlip && (
                            <button
                              onClick={() => handleDownload(order.screenShot)}
                              className="btn btn-view justify-center"
                            >
                              Download Slip
                            </button>
                          )}
                        </div>
                      </td>
                    )
                  ) : (
                    <td className="table-td capitalize">{order.paymentStatus}</td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Verify Payments</h2>
      </div>

      <div className="flex flex-wrap gap-3 my-5">
        {canViewPending && (
          <button
            onClick={() => setActiveTab("pending")}
            className={`btn justify-center ${
              activeTab === "pending" ? "btn-primary" : "btn-reset-filter"
            }`}
          >
            Pending Payments
          </button>
        )}
        {canViewApproved && (
          <button
            onClick={() => setActiveTab("approved")}
            className={`btn justify-center ${
              activeTab === "approved" ? "btn-primary" : "btn-reset-filter"
            }`}
          >
            Approved Payments
          </button>
        )}
        {canViewRejected && (
          <button
            onClick={() => setActiveTab("rejected")}
            className={`btn justify-center ${
              activeTab === "rejected" ? "btn-primary" : "btn-reset-filter"
            }`}
          >
            Rejected Payments
          </button>
        )}
      </div>

      {activeTab === "pending" && canViewPending && renderTable(verifiedPayments, true)}
      {activeTab === "approved" && canViewApproved && renderTable(acceptedPayments)}
      {activeTab === "rejected" && canViewRejected && renderTable(rejectedPayments)}

      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg w-96">
            <h3 className="text-xl font-bold text-center">{modalMessage}</h3>
            <div className="mt-4 flex justify-center gap-3">
              <button
                onClick={confirmAction}
                className="btn btn-primary justify-center"
              >
                Yes
              </button>
              <button
                onClick={cancelAction}
                className="btn btn-reset-filter justify-center"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VerifyPayments;
